import { Button, Eyebrow, GoldDivider } from '@tt/design-system';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { POINTS_BALANCE, POINTS_COST } from '../../../lib/env';
import { formatPoints } from '../../../utils/payments/mockPoints';
import { wait } from '../../../utils/wait';
import type { PaymentMethodProps } from '../types';

export function PointsMethod({ onConfirm, isConfirming = false }: PaymentMethodProps) {
  const reduced = usePrefersReducedMotion();
  const [redeeming, setRedeeming] = useState(false);

  const balance = POINTS_BALANCE;
  const cost = POINTS_COST;
  const remaining = Math.max(0, balance - cost);
  const insufficient = balance < cost;
  const progressPercent = balance > 0 ? Math.round((remaining / balance) * 100) : 0;

  async function handleRedeem() {
    if (insufficient || isConfirming || redeeming) return;
    setRedeeming(true);
    await wait(1000);
    setRedeeming(false);
    onConfirm({
      method: 'points',
      detailsFake: `${formatPoints(cost)} pontos resgatados`,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <Eyebrow tone="gold">Resgate com pontos</Eyebrow>

      <div className="flex flex-col gap-4 rounded-md border border-gold/20 bg-night/40 p-6">
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Saldo atual
          </span>
          <span className="font-display text-2xl text-parchment">
            {formatPoints(balance)} pts
          </span>
        </div>

        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Custo
          </span>
          <span className="font-display text-2xl text-gold-shimmer">
            −{formatPoints(cost)} pts
          </span>
        </div>

        <GoldDivider variant="line" />

        <div className="flex items-baseline justify-between">
          <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
            Saldo após resgate
          </span>
          <span className="font-display text-xl text-champagne">
            {formatPoints(remaining)} pts
          </span>
        </div>

        <div className="mt-2 flex flex-col gap-1">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-night ring-1 ring-gold/20"
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Saldo restante após o resgate"
          >
            <motion.div
              className="h-full bg-gold-gradient"
              initial={reduced ? false : { width: '100%' }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: reduced ? 0 : 0.7, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
          <p className="text-right font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
            {progressPercent}% do saldo após resgate
          </p>
        </div>
      </div>

      {insufficient ? (
        <p
          role="alert"
          className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error"
        >
          Saldo insuficiente para resgate.
        </p>
      ) : null}

      <div className="border-t border-gold/15 pt-6">
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleRedeem}
          disabled={insufficient || isConfirming || redeeming}
        >
          {isConfirming
            ? 'Eternizando seu registro…'
            : redeeming
              ? 'Resgatando pontos…'
              : 'Resgatar pontos'}
        </Button>
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
          Modo demonstração · saldo simulado
        </p>
      </div>
    </div>
  );
}
